import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
    GET_DAYCARE_MEAL_ACCESSIBILITY_SECTION,
    GET_DAYCARE_MEAL_HABITS,
    GET_DAYCARE_MEAL_MENUS,
    GET_DAYCARE_MEAL_TEXTURES,
    GET_MEAL_DETAIL_BY_TYPE
} from '@/modules/API/meal/getMealDaycareSettings'
import { FREEZE_MEAL_DETAIL } from '@/modules/API/meal/daycareSettingsHandler'
import {
    MealAccessibilitySection,
    MealDayDetailByType,
    MealDetail,
    MealHabit,
    MealMenu,
    MealTexture,
    MealType,
    MealTypeDetail
} from '@/types/Meal'
import { useNotification } from '@/modules/context/NotificationContext'
import { useTranslation } from 'react-i18next'
import { getMealDetailKey } from '@/container/meal/mealHandler/mealDetailRow'

interface UseMealDataProps {
    daycareId: number | undefined
    date: string
    defaultSections: string | null
    selectedSectionAndSectionGroup: string[]
    getSectionIdsFromSelection: (selection: string[]) => number[]
    setSelectedSectionAndSectionGroup: (selected: string[]) => void
}

interface UseMealDataReturn {
    loadingMealDetail: boolean
    isClosingDay: boolean
    mealAccessibilitySections: MealAccessibilitySection[]
    mealDayDetailByType: MealDayDetailByType | null
    availableMenus: MealMenu[]
    availableTextures: MealTexture[]
    availableHabits: MealHabit[]
    selectedSections: { id: number; name: string }[]
    selectedMealTypes: string[]
    setSelectedMealTypes: (types: string[]) => void
    availableMealTypes: MealType[]
    fetchMealDetail: (date: string, daycareId: number | undefined, sectionIds: number[], mealTypes?: string[]) => void
    refetchMealDetail: () => void
    handleFreezeMealType: (mealType: string, sectionIds: number[]) => void
    orderAdjustments: Map<string, number>
    handleAdjustOrder: (detail: MealDetail, delta: number, contextKey?: string) => void
    getOrderAdjustment: (detail: MealDetail, contextKey?: string) => number
}

export function useMealData({
    daycareId,
    date,
    defaultSections,
    selectedSectionAndSectionGroup,
    getSectionIdsFromSelection,
    setSelectedSectionAndSectionGroup
}: UseMealDataProps): UseMealDataReturn {
    const { t } = useTranslation()
    const { showNotificationError, showNotificationSuccess } = useNotification()

    const [
        getMealDetailByType,
        { loading: loadingMealDetailByType, data: dataMealDetailByType, error: errorMealDetailByType }
    ] = useLazyQuery(GET_MEAL_DETAIL_BY_TYPE)

    const [getMealAccessibility, { data: dataMealAccessibility }] = useLazyQuery(GET_DAYCARE_MEAL_ACCESSIBILITY_SECTION)
    const [getMealMenus, { data: dataMealMenus }] = useLazyQuery(GET_DAYCARE_MEAL_MENUS)
    const [getMealTextures, { data: dataMealTextures }] = useLazyQuery(GET_DAYCARE_MEAL_TEXTURES)
    const [getMealHabits, { data: dataMealHabits }] = useLazyQuery(GET_DAYCARE_MEAL_HABITS)

    const [freezeMutation] = useMutation(FREEZE_MEAL_DETAIL)

    const [mealAccessibilitySections, setMealAccessibilitySections] = useState<MealAccessibilitySection[]>([])
    const [isClosingDay, setIsClosingDay] = useState<boolean>(false)
    const [mealDayDetailByType, setMealDayDetailByType] = useState<MealDayDetailByType | null>(null)
    const [selectedMealTypes, setSelectedMealTypes] = useState<string[]>([])
    const [availableMenus, setAvailableMenus] = useState<MealMenu[]>([])
    const [availableTextures, setAvailableTextures] = useState<MealTexture[]>([])
    const [availableHabits, setAvailableHabits] = useState<MealHabit[]>([])

    const [orderAdjustments, setOrderAdjustments] = useState<Map<string, number>>(new Map())

    const resetAdjustments: () => void = useCallback((): void => {
        setOrderAdjustments(new Map())
    }, [])

    useEffect(() => {
        resetAdjustments()
    }, [date, selectedSectionAndSectionGroup, resetAdjustments])

    useEffect(() => {
        if (daycareId) {
            getMealAccessibility({ variables: { daycareId } })
            getMealMenus({ variables: { daycareId } })
            getMealTextures({ variables: { daycareId } })
            getMealHabits({ variables: { daycareId } })
        }
    }, [daycareId])

    useEffect(() => {
        if (dataMealAccessibility?.getDaycareMealAccessibility) {
            setMealAccessibilitySections(dataMealAccessibility.getDaycareMealAccessibility)
        }
    }, [dataMealAccessibility])

    useEffect(() => {
        if (dataMealMenus?.getDaycareMealMenus) {
            setAvailableMenus(dataMealMenus.getDaycareMealMenus)
        }
    }, [dataMealMenus])

    useEffect(() => {
        if (dataMealTextures?.getDaycareMealTextures) {
            setAvailableTextures(dataMealTextures.getDaycareMealTextures)
        }
    }, [dataMealTextures])

    useEffect(() => {
        if (dataMealHabits?.getDaycareMealHabits) {
            setAvailableHabits(dataMealHabits.getDaycareMealHabits)
        }
    }, [dataMealHabits])

    useEffect(() => {
        if (dataMealDetailByType?.getMealDetailByType) {
            const detailByType: MealDayDetailByType = dataMealDetailByType.getMealDetailByType
            setIsClosingDay(detailByType.closingDay || false)
            setMealDayDetailByType(detailByType)
            const newMap: Map<string, number> = new Map()
            for (const typeDetail of detailByType.typeDetails) {
                if (!typeDetail.frozen) {
                    const contextKey: string = `${typeDetail.type}_${typeDetail.sectionId}`
                    for (const d of typeDetail.details) {
                        if (d.quantityAdded > 0) {
                            newMap.set(`${contextKey}_${getMealDetailKey(d)}`, d.quantityAdded)
                        }
                    }
                }
            }
            setOrderAdjustments(newMap)
        }
    }, [dataMealDetailByType])

    useEffect(() => {
        if (errorMealDetailByType) {
            showNotificationError(t('mealLoadedError'))
            setMealDayDetailByType(null)
        }
    }, [errorMealDetailByType, showNotificationError, t])

    useEffect(() => {
        if (mealAccessibilitySections.length > 0) {
            const allSectionIds: string[] = mealAccessibilitySections.map(
                (s: MealAccessibilitySection): string => 'SECTION_' + s.sectionId.toString()
            )
            const uniqueSectionIds: string[] = [...new Set(allSectionIds)]

            let selectedIds: string[] = uniqueSectionIds
            if (defaultSections) {
                const urlSectionIds: string[] = defaultSections
                    .split(',')
                    .map((id: string): string => 'SECTION_' + id.trim())
                const validUrlSectionIds: string[] = urlSectionIds.filter((id: string): boolean =>
                    uniqueSectionIds.includes(id)
                )
                if (validUrlSectionIds.length > 0) {
                    selectedIds = validUrlSectionIds
                }
            }

            setSelectedSectionAndSectionGroup(selectedIds)

            if (selectedIds.length > 0) {
                getMealDetailByType({
                    variables: {
                        date: date,
                        daycareId: daycareId,
                        sectionIds: getSectionIdsFromSelection(selectedIds),
                        mealTypes: selectedMealTypes.length > 0 ? selectedMealTypes : undefined
                    }
                })
            }
        }
    }, [mealAccessibilitySections, daycareId])

    const availableMealTypes: MealType[] = [
        ...new Set(
            mealAccessibilitySections
                .filter((s: MealAccessibilitySection): boolean => {
                    const sectionIds: number[] = getSectionIdsFromSelection(selectedSectionAndSectionGroup)
                    return sectionIds.length === 0 || sectionIds.includes(s.sectionId)
                })
                .map((s: MealAccessibilitySection): MealType => s.type)
        )
    ]

    useEffect(() => {
        if (availableMealTypes.length > 0 && selectedMealTypes.length === 0) {
            setSelectedMealTypes(availableMealTypes)
        } else if (selectedMealTypes.length > 0) {
            const filtered: string[] = selectedMealTypes.filter((t: string): boolean =>
                availableMealTypes.includes(t as MealType)
            )
            if (filtered.length !== selectedMealTypes.length) {
                setSelectedMealTypes(filtered.length > 0 ? filtered : availableMealTypes)
            }
        }
    }, [availableMealTypes.join(',')])

    function fetchMealDetail(
        fetchDate: string,
        fetchDaycareId: number | undefined,
        sectionIds: number[],
        mealTypes?: string[]
    ): void {
        if (sectionIds.length > 0) {
            const types: string[] = mealTypes ?? selectedMealTypes
            getMealDetailByType({
                variables: {
                    date: fetchDate,
                    daycareId: fetchDaycareId,
                    sectionIds: sectionIds,
                    mealTypes: types.length > 0 ? types : undefined
                }
            })
        }
    }

    function refetchMealDetail(): void {
        const sectionIds: number[] = getSectionIdsFromSelection(selectedSectionAndSectionGroup)
        fetchMealDetail(date, daycareId, sectionIds)
    }

    const selectedSections: { id: number; name: string }[] = mealAccessibilitySections.reduce(
        (acc: { id: number; name: string }[], section: MealAccessibilitySection) => {
            const sectionIds: number[] = getSectionIdsFromSelection(selectedSectionAndSectionGroup)
            if (sectionIds.includes(section.sectionId) && !acc.some((s) => s.id === section.sectionId)) {
                acc.push({ id: section.sectionId, name: section.sectionName })
            }
            return acc
        },
        []
    )

    function getAdjustmentKey(detail: MealDetail, contextKey?: string): string {
        const combinationKey: string = getMealDetailKey(detail)
        return contextKey ? `${contextKey}_${combinationKey}` : combinationKey
    }

    function getOrderAdjustment(detail: MealDetail, contextKey?: string): number {
        return orderAdjustments.get(getAdjustmentKey(detail, contextKey)) ?? 0
    }

    function handleAdjustOrder(detail: MealDetail, delta: number, contextKey?: string): void {
        const key: string = getAdjustmentKey(detail, contextKey)
        const currentAdjustment: number = orderAdjustments.get(key) ?? 0
        const newAdjustment: number = currentAdjustment + delta
        const toOrder: number = detail.quantityReserved.length + newAdjustment
        if (toOrder < 0) return

        setOrderAdjustments((prev: Map<string, number>) => {
            const next: Map<string, number> = new Map(prev)
            next.set(key, newAdjustment)
            return next
        })
    }

    function handleFreezeMealType(mealType: string, sectionIds: number[]): void {
        const typeDetails: MealTypeDetail[] =
            mealDayDetailByType?.typeDetails.filter(
                (td: MealTypeDetail): boolean => td.type === mealType && sectionIds.includes(td.sectionId)
            ) ?? []
        const allFrozen: boolean = typeDetails.every((td: MealTypeDetail): boolean => td.frozen)
        const shouldFreeze: boolean = !allFrozen

        let adjustments:
            | { menuIds: number[]; textureIds: number[]; habitIds: number[]; adjustment: number }[]
            | undefined
        if (shouldFreeze) {
            adjustments = []
            const seenKeys: Set<string> = new Set()
            for (const td of typeDetails) {
                if (td.frozen) continue
                for (const detail of td.details) {
                    const combinationKey: string = getMealDetailKey(detail)
                    if (seenKeys.has(combinationKey)) continue
                    seenKeys.add(combinationKey)
                    const contextKey: string = `${mealType}_${td.sectionId}_${combinationKey}`
                    const adj: number = orderAdjustments.get(contextKey) ?? 0
                    if (adj !== 0 || detail.quantityAdded !== 0) {
                        adjustments.push({
                            menuIds: detail.combination.menus.map((m: MealMenu): number => m.id),
                            textureIds: detail.combination.textures.map((t: MealTexture): number => t.id),
                            habitIds: detail.combination.habits.map((h: MealHabit): number => h.id),
                            adjustment: adj
                        })
                    }
                }
            }
        }

        freezeMutation({
            variables: {
                daycareId: daycareId,
                sectionIds: sectionIds,
                date: date,
                freeze: shouldFreeze,
                mealTypes: [mealType],
                orderAdjustments: adjustments && adjustments.length > 0 ? adjustments : undefined
            },
            onCompleted: () => {
                resetAdjustments()
                refetchMealDetail()
                showNotificationSuccess(allFrozen ? t('mealUnfrozen') : t('mealFrozen'))
            },
            onError: () => {
                showNotificationError(allFrozen ? t('mealUnfrozenError') : t('mealFrozenError'))
            }
        })
    }

    return {
        loadingMealDetail: loadingMealDetailByType,
        isClosingDay,
        mealAccessibilitySections,
        mealDayDetailByType,
        availableMenus,
        availableTextures,
        availableHabits,
        selectedSections,
        selectedMealTypes,
        setSelectedMealTypes,
        availableMealTypes,
        fetchMealDetail,
        refetchMealDetail,
        handleFreezeMealType,
        orderAdjustments,
        handleAdjustOrder,
        getOrderAdjustment
    }
}
